import React, { useState, useRef, useEffect, useCallback } from "react";
import mapboxgl from "mapbox-gl";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import DrawRectangle from "mapbox-gl-draw-rectangle-mode";
import { produce } from "immer";
import "mapbox-gl/dist/mapbox-gl.css";

import {
  withCustomGlyphsMapOptions,
  withCustomGlyphsStyle,
} from "#utils/mapboxGlyphs";
import { getLayerName } from "./utils";
import { Layer, Sources, Source } from "./type";
import { MapChildContext } from "./context";

const UNSUPPORTED_BROWSER = !mapboxgl.supported();
const { VITE_APP_MAPBOX_ACCESS_TOKEN: TOKEN } = import.meta.env;
if (TOKEN) {
  mapboxgl.accessToken = TOKEN;
}

type Position = "top-right" | "top-left" | "bottom-right" | "bottom-left";

interface LastIn {
  id: string | number | undefined;
  layerName: string;
  sourceName: string;
  sourceLayer: string | undefined;
}

interface ExtendedLayer extends Layer {
  layerKey: string;
  sourceKey: string;
}

function findLayerFromLayers(layers: ExtendedLayer[], layerKey: string) {
  const layer = layers.find((l) => l.layerKey === layerKey);
  return layer;
}

function getLayersForSources(sources: Sources) {
  const layers = Object.entries(sources)
    .filter(([_, source]) => !!source.layers)
    .map(([sourceKey, source]) =>
      Object.entries(source.layers).map(([layerKey, layer]) => ({
        ...layer,
        sourceKey,
        layerKey: getLayerName(sourceKey, layerKey),
      }))
    )
    .flat();
  return layers;
}

function noop() {}

interface Props {
  mapStyle: mapboxgl.MapboxOptions["style"];
  mapOptions: Omit<mapboxgl.MapboxOptions, "style" | "container">;

  scaleControlShown: boolean;
  scaleControlPosition?: Position;
  scaleControlOptions?: ConstructorParameters<typeof mapboxgl.ScaleControl>[0];

  navControlShown: boolean;
  navControlPosition?: Position;
  navControlOptions?: ConstructorParameters<
    typeof mapboxgl.NavigationControl
  >[0];

  debug?: boolean;
  onMapLoad?: (map: mapboxgl.Map) => void;
  drawRefState: boolean;
  resetDrawState: () => void;
  geoLocationRef?: React.RefObject<mapboxgl.GeolocateControl>;
  handleTilesLoad?: (loaded: boolean) => void;
}

const Map: React.FC<Props> = (props) => {
  const {
    mapStyle: mapStyleFromProps,
    mapOptions,
    scaleControlPosition,
    scaleControlShown,
    scaleControlOptions,
    navControlShown,
    navControlOptions,
    navControlPosition,
    children,
    clickHandler,
    debug,
    handleMapClicked,
    onMapLoad,
    drawRefState,
    resetDrawState,
    geoLocationRef,
    handleTilesLoad,
  } = props;

  const [initialDebug] = useState(debug);

  const [mapStyle, setMapStyle] =
    useState<mapboxgl.MapboxOptions["style"]>(undefined);
  const [loaded, setLoaded] = useState<boolean>(false);

  const boundsRef = useRef<[number, number, number, number] | undefined>();
  const paddingRef = useRef<number | undefined>();
  const durationRef = useRef<number | undefined>();

  const lastIn = useRef<LastIn | undefined>(undefined);
  const mapDestroyedRef = useRef(false);
  const sourcesRef = useRef<Sources>({});
  const mapRef = useRef<mapboxgl.Map | undefined>(undefined);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const drawRef = useRef<MapboxDraw | null>(null);

  const setBounds = useCallback(
    (
      bounds: [number, number, number, number] | undefined,
      padding: number | undefined,
      duration: number | undefined
    ) => {
      boundsRef.current = bounds;
      paddingRef.current = padding;
      durationRef.current = duration;
    },
    []
  );

  // Create map
  useEffect(() => {
    if (UNSUPPORTED_BROWSER) {
      console.error("No Mapboxgl support.");
      return noop;
    }
    const { current: mapContainer } = mapContainerRef;
    if (!mapContainer) {
      console.error("No container found.");
      return noop;
    }

    const mapboxglMap = new mapboxgl.Map(
      withCustomGlyphsMapOptions({
        container: mapContainer,
        style: mapStyleFromProps,
        accessToken: TOKEN,
        preserveDrawingBuffer: true,
        ...mapOptions,
      })
    );

    mapRef.current = mapboxglMap;
    mapboxglMap.getCanvas().style.cursor = "default";

    if (scaleControlShown) {
      const scale = new mapboxgl.ScaleControl(scaleControlOptions);
      mapboxglMap.addControl(scale, scaleControlPosition);
    }

    if (navControlShown) {
      // NOTE: don't we need to remove control on unmount?
      const nav = new mapboxgl.NavigationControl(navControlOptions);
      mapboxglMap.addControl(nav, navControlPosition);
    }

    /*
            // TODO: need to resize map in some cases
            const timer = setTimeout(() => {
                mapboxglMap.resize();
            }, 200);
            */

    const handleClick = (data: mapboxgl.MapMouseEvent & mapboxgl.EventData) => {
      if (!mapRef.current) {
        return;
      }

      if (clickHandler) {
        clickHandler(data);
        handleMapClicked(data);
      }
      const layers = getLayersForSources(sourcesRef.current);

      const { point, lngLat } = data;

      const clickableLayerKeys = layers
        .filter((layer) => !!layer.onClick)
        .map((layer) => layer.layerKey);

      const clickableFeatures = mapRef.current.queryRenderedFeatures(point, {
        layers: clickableLayerKeys,
      });

      if (clickableFeatures.length <= 0) {
        // TODO: add a global handler
        if (clickableLayerKeys.length > 0) {
          const currentlayer =
            clickableLayerKeys[clickableLayerKeys.length - 1];
          const layer = findLayerFromLayers(layers, currentlayer.name);
          clickableFeatures.every((clickableFeature) => {
            if (layer && layer.onClick) {
              return !layer.onClick(clickableFeature, lngLat, point);
            }
            return null;
          });

          return;
        }
      }
      clickableFeatures.every((clickableFeature) => {
        const {
          layer: { id },
        } = clickableFeature;
        const layer = findLayerFromLayers(layers, id);
        if (layer && layer.onClick) {
          return !layer.onClick(clickableFeature, lngLat, point);
        }
        return false;
      });
    };

    const handleDoubleClick = (
      data: mapboxgl.MapMouseEvent & mapboxgl.EventData
    ) => {
      if (!mapRef.current) {
        return;
      }

      const layers = getLayersForSources(sourcesRef.current);

      const { point, lngLat } = data;

      const clickableLayerKeys = layers
        .filter((layer) => !!layer.onDoubleClick)
        .map((layer) => layer.layerKey);

      const clickableFeatures = mapRef.current.queryRenderedFeatures(point, {
        layers: clickableLayerKeys,
      });

      if (clickableFeatures.length <= 0) {
        console.warn("No clickable layer found.");
        // TODO: add a global handler
        return;
      }
      clickableFeatures.every((clickableFeature) => {
        const {
          layer: { id },
        } = clickableFeature;

        const layer = findLayerFromLayers(layers, id);
        if (layer && layer.onDoubleClick) {
          return !layer.onDoubleClick(clickableFeature, lngLat, point);
        }
        return false;
      });
    };

    const handleMouseMove = (
      data: mapboxgl.MapMouseEvent & mapboxgl.EventData
    ) => {
      if (!mapRef.current) {
        return;
      }

      const layers = getLayersForSources(sourcesRef.current);

      const { point, lngLat } = data;

      if (!drawRef.current) {
        const interactiveLayerKeys = layers
          .filter((layer) => !!layer.onClick || !!layer.onDoubleClick)
          .map((layer) => layer.layerKey)
          .filter((layerKey) => mapRef.current?.getLayer(layerKey));

        const interactiveFeatures =
          interactiveLayerKeys.length > 0
            ? mapRef.current.queryRenderedFeatures(point, {
                layers: interactiveLayerKeys,
              })
            : [];

        mapboxglMap.getCanvas().style.cursor =
          interactiveFeatures.length > 0 ? "pointer" : "default";
      }

      const hoverableLayerKeys = layers
        .filter((layer) => !!layer.onMouseEnter || !!layer.onMouseLeave)
        .map((layer) => layer.layerKey);

      const hoverableFeatures = mapRef.current.queryRenderedFeatures(point, {
        layers: hoverableLayerKeys,
      });

      if (hoverableFeatures.length <= 0) {
        if (lastIn.current) {
          mapboxglMap.removeFeatureState(
            {
              id: lastIn.current.id,
              source: lastIn.current.sourceName,
              sourceLayer: lastIn.current.sourceLayer,
            },
            "hovered"
          );
          const layer = findLayerFromLayers(layers, lastIn.current.layerName);
          if (layer && layer.onMouseLeave) {
            layer.onMouseLeave();
          }
        }
        lastIn.current = undefined;
        return;
      }

      const firstFeature = hoverableFeatures[0];
      if (
        !lastIn.current ||
        firstFeature.source !== lastIn.current.sourceName ||
        firstFeature.sourceLayer !== lastIn.current.sourceLayer ||
        firstFeature.layer.id !== lastIn.current.layerName ||
        firstFeature.id !== lastIn.current.id
      ) {
        if (lastIn.current) {
          mapboxglMap.removeFeatureState(
            {
              id: lastIn.current.id,
              source: lastIn.current.sourceName,
              sourceLayer: lastIn.current.sourceLayer,
            },
            "hovered"
          );
        }
        if (
          lastIn.current &&
          (firstFeature.source !== lastIn.current.sourceName ||
            firstFeature.sourceLayer !== lastIn.current.sourceLayer ||
            firstFeature.layer.id !== lastIn.current.layerName)
        ) {
          const layer = findLayerFromLayers(layers, lastIn.current.layerName);
          if (layer && layer.onMouseLeave) {
            layer.onMouseLeave();
          }
        }

        lastIn.current = {
          id: firstFeature.id,
          layerName: firstFeature.layer.id,
          sourceName: firstFeature.source,
          sourceLayer: firstFeature.sourceLayer,
        };

        mapboxglMap.setFeatureState(
          {
            id: lastIn.current.id,
            source: lastIn.current.sourceName,
            sourceLayer: lastIn.current.sourceLayer,
          },
          { hovered: true }
        );

        const {
          layer: { id },
        } = firstFeature;
        const layer = findLayerFromLayers(layers, id);
        if (layer && layer.onMouseEnter) {
          layer.onMouseEnter(firstFeature, lngLat, point);
        }
      }
    };

    const handleResize = () => {
      if (!mapRef.current) {
        return;
      }
      if (!boundsRef.current) {
        return;
      }
      // NOTE: just to be safe here
      if (boundsRef.current.length < 4) {
        return;
      }

      const [fooLon, fooLat, barLon, barLat] = boundsRef.current;
      mapRef.current.fitBounds(
        [
          [fooLon, fooLat],
          [barLon, barLat],
        ],
        {
          padding: paddingRef.current,
          duration: durationRef.current,
        }
      );
    };

    if (geoLocationRef) {
      const geo = new mapboxgl.GeolocateControl({
        positionOptions: {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        },
        trackUserLocation: true,
        showUserLocation: true,
        showAccuracyCircle: true,
      });

      mapboxglMap.addControl(geo, "top-right");
      geoLocationRef.current = geo;

      geo.on("geolocate", (e: any) => {
        console.log("GEOLOCATION SUCCESS:", e.coords);
        mapboxglMap.flyTo({
          center: [e.coords.longitude, e.coords.latitude],
          zoom: 15,
          duration: 1500,
        });
      });

      geo.on("error", (err: any) => {
        console.error("Geolocation error:", err);
        alert("Location failed. Try again or check your connection.");
      });
    }

    mapboxglMap.on("click", handleClick);
    mapboxglMap.on("dblclick", handleDoubleClick);
    mapboxglMap.on("mousemove", handleMouseMove);
    mapboxglMap.on("resize", handleResize);

    const destroy = () => {
      // clearTimeout(timer);

      mapboxglMap.off("click", handleClick);
      mapboxglMap.off("dblclick", handleDoubleClick);
      mapboxglMap.off("mousemove", handleMouseMove);
      mapboxglMap.off("resize", handleResize);

      sourcesRef.current = {};
      lastIn.current = undefined;
      mapDestroyedRef.current = true;

      if (initialDebug) {
        console.warn("Removing map");
      }
      mapboxglMap.remove();
    };

    return destroy;
  }, [initialDebug, geoLocationRef]);

  // useing 'idle' event:
  useEffect(() => {
    if (!mapRef.current || !handleTilesLoad) {
      return noop;
    }

    const onDataLoading = () => {
      handleTilesLoad(false);
    };

    const onIdle = () => {
      // Map is idle when all tiles and data are loaded
      handleTilesLoad(true);
    };

    // Set to false when data starts loading
    mapRef.current.on("dataloading", onDataLoading);

    // Set to true when map becomes idle (all tiles loaded)
    mapRef.current.on("idle", onIdle);

    return () => {
      if (mapRef.current) {
        mapRef.current.off("dataloading", onDataLoading);
        mapRef.current.off("idle", onIdle);
      }
    };
  }, [handleTilesLoad, loaded]);

  // Handle style load and map ready
  useEffect(() => {
    if (UNSUPPORTED_BROWSER || !mapRef.current || !mapStyleFromProps) {
      return noop;
    }
    sourcesRef.current = {};
    lastIn.current = undefined;

    if (initialDebug) {
      console.warn(`Setting map style ${mapStyleFromProps}`);
    }
    const mapStyleWithCustomGlyphs = withCustomGlyphsStyle(mapStyleFromProps);
    mapRef.current.setStyle(mapStyleWithCustomGlyphs);

    const onStyleData = () => {
      if (initialDebug) {
        console.info("Passing mapStyle:", mapStyleWithCustomGlyphs);
      }
      setMapStyle(mapStyleWithCustomGlyphs);
    };
    mapRef.current.once("styledata", onStyleData);

    const onLoad = () => {
      setLoaded(true);
      if (onMapLoad) {
        onMapLoad(mapRef.current!);
      }
    };
    mapRef.current.once("load", onLoad);

    return () => {
      if (mapRef.current) {
        mapRef.current.off("styledata", onStyleData);
        mapRef.current.off("load", onLoad);
      }
    };
  }, [mapStyleFromProps, initialDebug, onMapLoad]);

  // Rectangle Draw Mode Effect
  useEffect(() => {
    if (!mapRef.current) return;

    // Turn OFF draw mode
    if (!drawRefState) {
      if (drawRef.current) {
        mapRef.current.removeControl(drawRef.current);
        drawRef.current = null;
        mapRef.current.getCanvas().style.cursor = "default";
        console.log("Draw mode deactivated");
      }
      return;
    }

    // Turn ON draw mode with RECTANGLE
    if (!drawRef.current) {
      const modes = MapboxDraw.modes;
      modes.draw_rectangle = DrawRectangle;

      const draw = new MapboxDraw({
        displayControlsDefault: false,
        modes,
        defaultMode: "draw_rectangle",
      });

      drawRef.current = draw;
      mapRef.current.addControl(draw, "top-left");
      console.log("Rectangle draw mode activated");
    }

    // Handle rectangle creation
    const handleDrawCreate = (e: any) => {
      console.log("Draw create event fired!", e);

      // Check if map is available
      if (!mapRef.current) {
        console.error("Map reference is null!");
        return;
      }

      if (!e.features || e.features.length === 0) {
        console.log("No features in event");
        return;
      }

      const feature = e.features[0];
      console.log("Feature:", feature);

      if (!feature || feature.geometry.type !== "Polygon") {
        console.log("Not a polygon");
        return;
      }

      const coords = feature.geometry.coordinates[0];
      console.log("Coordinates:", coords);

      if (!coords || coords.length < 4) {
        console.log("Invalid coordinates");
        return;
      }

      // Extract bounds from rectangle coordinates
      const lngs = coords.map((c: number[]) => c[0]);
      const lats = coords.map((c: number[]) => c[1]);

      const minLng = Math.min(...lngs);
      const maxLng = Math.max(...lngs);
      const minLat = Math.min(...lats);
      const maxLat = Math.max(...lats);

      console.log("Calculated bounds:", { minLng, maxLng, minLat, maxLat });

      // Store reference to map to ensure it doesn't become null
      const map = mapRef.current;

      // CRITICAL: Delete the rectangle FIRST before zooming
      // This prevents any conflicts with the draw plugin
      if (drawRef.current) {
        drawRef.current.deleteAll();
      }

      // Small delay to ensure deletion completes
      setTimeout(() => {
        console.log("Starting zoom animation...");

        // Create LngLatBounds object explicitly
        const bounds = new mapboxgl.LngLatBounds(
          [minLng, minLat],
          [maxLng, maxLat]
        );

        console.log("LngLatBounds object:", bounds);

        try {
          // Try flyTo which is more reliable than fitBounds
          const center = bounds.getCenter();

          // Calculate appropriate zoom level
          const canvas = map.getCanvas();
          const w = canvas.width;
          const h = canvas.height;

          // Get bounds in pixel coordinates
          const nw = map.project([minLng, maxLat]);
          const se = map.project([maxLng, minLat]);

          const size = se.sub(nw);
          const scaleX = (w - 120) / size.x; // 120 = padding * 2
          const scaleY = (h - 120) / size.y;
          const scale = Math.min(scaleX, scaleY);

          const zoom = Math.min(map.getZoom() + Math.log2(scale), 18);

          console.log("Flying to center:", center, "zoom:", zoom);

          map.flyTo({
            center: [center.lng, center.lat],
            zoom: zoom,
            duration: 1500,
            essential: true,
          });

          console.log("FlyTo called successfully!");

          // Exit draw mode after zoom completes
          setTimeout(() => {
            resetDrawState();
          }, 1600);
        } catch (error) {
          console.error("Error zooming:", error);
          // Still exit draw mode even on error
          resetDrawState();
        }
      }, 100);
    };

    // Try multiple events to catch the rectangle completion
    const handleDrawUpdate = (e: any) => {
      console.log("Draw update event fired!", e);
      handleDrawCreate(e);
    };

    const handleDrawModeChange = (e: any) => {
      console.log("Draw mode change:", e);
    };

    const handleDrawRender = (e: any) => {
      console.log("Draw render:", e);
    };

    mapRef.current.on("draw.create", handleDrawCreate);
    mapRef.current.on("draw.update", handleDrawUpdate);
    mapRef.current.on("draw.modechange", handleDrawModeChange);
    mapRef.current.on("draw.render", handleDrawRender);

    return () => {
      if (mapRef.current) {
        mapRef.current.off("draw.create", handleDrawCreate);
        mapRef.current.off("draw.update", handleDrawUpdate);
        mapRef.current.off("draw.modechange", handleDrawModeChange);
        mapRef.current.off("draw.render", handleDrawRender);
      }
      if (drawRef.current) {
        mapRef.current!.removeControl(drawRef.current);
        drawRef.current = null;
      }
    };
  }, [drawRefState, resetDrawState]);

  const isMapDestroyed = useCallback(() => !!mapDestroyedRef.current, []);

  const isSourceDefined = useCallback(
    (sourceKey: string) => !!sourcesRef.current[sourceKey],
    []
  );

  const getSource = useCallback(
    (sourceKey: string) => sourcesRef.current[sourceKey],
    []
  );

  const setSource = useCallback((source: Source) => {
    sourcesRef.current = produce(sourcesRef.current, (safeSource) => {
      const { name } = source;

      safeSource[name] = source;
    });
  }, []);

  const removeSource = useCallback(
    (sourceKey: string) => {
      if (!sourcesRef.current[sourceKey]) {
        return;
      }

      sourcesRef.current = produce(sourcesRef.current, (safeSource) => {
        delete safeSource[sourceKey];
      });

      if (mapRef.current) {
        if (initialDebug) {
          console.warn(`Removing source: ${sourceKey}`);
        }
        mapRef.current.removeSource(sourceKey);
      }
    },
    [initialDebug]
  );

  const mapChildren = children as React.ReactElement<unknown>;
  if (UNSUPPORTED_BROWSER) {
    return mapChildren;
  }

  const childrenProps = {
    map: mapRef.current,
    mapStyle: loaded ? mapStyle : undefined,
    mapContainerRef,

    isSourceDefined,
    getSource,
    setSource,
    removeSource,

    isMapDestroyed,

    setBounds,
    debug: initialDebug,
  };

  return (
    <MapChildContext.Provider value={childrenProps}>
      {mapChildren}
    </MapChildContext.Provider>
  );
};

Map.defaultProps = {
  scaleControlShown: false,
  scaleControlPosition: "bottom-right",

  navControlShown: false,
  navControlPosition: "top-right",

  debug: false,
};

export default Map;
